import { defineStore } from 'pinia';
import { subscribeToAddOn } from '@webapp-next/lib/request/webapp/add-ons/subscribe-to-add-on.js';
import {
  ADD_ON_LEARN_MORE_URLS,
  addOnDataMap,
  addOns,
  annualAddOns,
  EVENTS,
  NEWSFEED_EVENTS,
  OnceRoutes,
  SHARED_NEWSFEED_POST_TYPE,
  SUBSCRIPTION,
  SUBSCRIPTION_CHANNEL,
  SUBSCRIPTION_NAME_TRACKING,
  SubscriptionErrorToLocaleKey,
} from '@/apps/billing-shared/src/definitions';
import { getAddOnSubscriptions } from '@webapp-next/lib/request/webapp/add-ons/get-add-on-subscriptions.js';
import getInstallationCountryCode from '@webapp-next/lib/facades/get-installation-country.js';
import getPlanId from '@webapp-next/lib/facades/get-plan-id.js';
import { usePricingStore } from '@/apps/billing-shared/src/store/pricing';
import checkFF from '@/lib/feature-flag';
import {
  isFeatureFlagDateInFuture,
  getFeatureFlagDate,
  getFeatureFlagDateDiffInDays,
} from '@/apps/once/src/util/pre-subscribed-validation';
import {
  isEnterprise,
  isPaid,
  isProTrial,
} from '@webapp-next/lib/company-subscription';
import userHasPermission from '@webapp-next/lib/user-has-permission';
import { getDataAnalyticsService } from '@webapp-next/lib/tracking/get-data-analytics';
import { getAddOnTrialDaysRemaining } from '@/apps/billing-shared/src/util/get-add-on-trial-days-remaining';
import { addDaysToDate } from '@/apps/billing-shared/src/util/add-days-to-date';
import { ADD_ONS } from '@/modules/trial-onboarding/definitions';
import useWebappStore from '@webapp-next/store';
import { formatDisplayDateShort } from '@/apps/billing-shared/src/util/format-display-date-short';
import {
  shouldEnableHrBeta,
  shouldEnableHrPaywallLogic,
} from '@apps-hr/lib/request/utils/hr-feature-flags';
import { getTranslations } from '@/lib/i18next';
import { getRouter } from '@webapp-next/router/helpers';
import getVueEventBus from '@webapp-next/lib/vue-event-bus';
import { DateTime } from 'luxon';
import { useAddOnStore } from '@apps-once/store/add-on';
import getInstallationDetails from '@/lib/facades/get-installation-details';

const VueEventBus = getVueEventBus();

// exposed for unit test
export const t = getTranslations({
  module: 'shared',
  file: 'tenant-subscription',
});

export const AddOnStatus = {
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
  MIGRATION_REQUIRED: 'MIGRATION_REQUIRED',
  INELIGIBLE: 'INELIGIBLE',
};

const ADD_ON_PROMO_CODES = {
  [addOns.HR_ADD_ON_ID]: [
    {
      code: '20OFFHR3M',
      discount: 20,
      chargeRatio: 0.8,
      discountRatio: 0.2,
      periodMonth: 3,
    },
    {
      code: 'HRSP20',
      discount: 20,
      chargeRatio: 0.8,
      discountRatio: 0.2,
      periodMonth: 3,
    },
    {
      code: 'HR15SIX',
      discount: 15,
      chargeRatio: 0.85,
      discountRatio: 0.15,
      periodMonth: 6,
    },
    {
      code: 'HR30EOY',
      discount: 30,
      chargeRatio: 0.7,
      discountRatio: 0.3,
      periodMonth: 3,
    },
    {
      code: '30DEPUTYHR',
      discount: 30,
      chargeRatio: 0.7,
      discountRatio: 0.3,
      periodMonth: 12,
    },
    {
      code: 'HR3FREE',
      discount: 100,
      chargeRatio: 0,
      discountRatio: 1,
      periodMonth: 3,
    },
    {
      code: 'HRFREE2',
      discount: 100,
      chargeRatio: 0,
      discountRatio: 1,
      periodMonth: 3,
    },
    {
      code: 'HRSP100',
      discount: 100,
      chargeRatio: 0,
      discountRatio: 1,
      periodMonth: 3,
    },
  ],
  [annualAddOns.HR_ANNUAL_ADD_ON_ID]: [
    {
      code: 'HRANNUAL1',
      discount: 25,
      chargeRatio: 0.75,
      discountRatio: 0.25,
      periodMonth: 12,
    },
    {
      code: 'HRANNUAL2',
      discount: 25,
      chargeRatio: 0.75,
      discountRatio: 0.25,
      periodMonth: 12,
    },
    {
      code: 'HRANNUAL3',
      discount: 25,
      chargeRatio: 0.75,
      discountRatio: 0.25,
      periodMonth: 12,
    },
  ],
  [addOns.ADVANCED_REPORTING_ADD_ON_ID]: [],
  [addOns.PAYROLL_ADD_ON_ID]: [],
};

const useTenantSubscriptionStore = defineStore('subscription', {
  state: () => {
    return {
      isSubscribing: false,
      isLoading: false,
      subscribeError: null,
      loadingError: null,
      loadAddOnSubscriptionsPromise: null,
      addOns: [],
      planId: getPlanId(),
      countryCode: getInstallationCountryCode(),
      subscription: {},
    };
  },
  getters: {
    data: () => ADD_ONS,
    isFlexi: state => {
      return () => {
        return state.planId == SUBSCRIPTION.FLEXI;
      };
    },
    hasCustomPricing: () => {
      return () => {
        return isEnterprise() || useTenantSubscriptionStore().isCPQCustomer();
      };
    },
    getAddOnPricing: state => {
      return addOnId => {
        const countryCode = state.countryCode;
        const planId = state.planId;
        const pricingStore = usePricingStore();
        return pricingStore.getAddOnPricing(countryCode, planId, addOnId);
      };
    },
    availableAddOns: () => {
      return useTenantSubscriptionStore().data.filter(addOn => {
        if (addOn.featureFlag && addOn.featureFlag !== '') {
          if (!checkFF(addOn.featureFlag)) {
            return false;
          }
        }

        return useTenantSubscriptionStore().getAddOnPricing(addOn.addOnId);
      });
    },
    hasAvailableAddOns() {
      return !!useTenantSubscriptionStore().availableAddOns?.length;
    },
    getSubscriptionDisplayPrice: () => {
      return addOnId => {
        const addOnPricing =
          useTenantSubscriptionStore().getAddOnPricing(addOnId);

        if (!addOnPricing?.pricing.price) {
          return;
        }

        return (
          addOnPricing?.currency.symbol + addOnPricing?.pricing.price ||
          addOnPricing?.customPricing
        );
      };
    },
    getInactiveUsersDisplayPrice: () => {
      return addOnId => {
        const addOnPricing =
          useTenantSubscriptionStore().getAddOnPricing(addOnId);
        return (
          parseFloat(addOnPricing?.pricing.priceInactive) * 100 +
          addOnPricing?.currency.decimalSymbol
        );
      };
    },
    isSubscribingToAddOn: state => {
      return state.isSubscribing;
    },

    // Check if the user is subscribed to the add-on or any related add-on from the same parent.
    isSubscribedToAddOn: state => {
      return addOnId => {
        const addOn = state.addOns[addOnId];
        if (!addOn) {
          return false;
        }

        if (addOn?.status === AddOnStatus.ACTIVE) {
          return true;
        }

        const parentAddOn = addOn?.parentAddOn;
        if (!parentAddOn) {
          return false;
        }

        // Check if any other add-on with the same parent is active
        return Object.values(state.addOns).some(
          otherAddOn =>
            otherAddOn.parentAddOn === parentAddOn &&
            otherAddOn.status === AddOnStatus.ACTIVE
        );
      };
    },
    canUserManageSubscription: () => {
      return () => {
        return (
          userHasPermission('ADMINISTRATOR') &&
          !useTenantSubscriptionStore().hasCustomPricing()
        );
      };
    },
    canCPQUserManageSubscription: () => {
      return () => {
        return (
          userHasPermission('ADMINISTRATOR') &&
          checkFF('wo-2737-cpq-add-on-trial-self-serve') &&
          !isEnterprise() &&
          useTenantSubscriptionStore().isCPQCustomer()
        );
      };
    },

    // Used to check if the add-on is available for purchase
    // Eg invoker: frontend/vnext/src/apps/hr/src/modules/upgrade/components/hr-upgrade-pricing-changes-banner.vue
    isAddOnAvailableForPurchase: state => {
      return addOnId => {
        // Early return if not on a paid plan
        if (!isPaid()) {
          return false;
        }

        const addOn = state.addOns[addOnId];
        if (!addOn) {
          return false;
        }

        const relatedAddOnIds =
          useAddOnStore().getRelatedAddOnIdsOrJustTheAddOnId(addOnId);

        // loop through all related add-on IDs to check if any of them is available for purchase
        return relatedAddOnIds.some(relatedAddOnId => {
          const relatedAddOn = state.addOns[relatedAddOnId];
          if (relatedAddOn?.status === AddOnStatus.INELIGIBLE) {
            return false;
          }

          if (
            useTenantSubscriptionStore().getSubscriptionDisplayPrice(
              relatedAddOnId
            ) &&
            !useTenantSubscriptionStore().isSubscribedToAddOn(relatedAddOnId)
          ) {
            return true;
          }
        });
      };
    },
    isUpSellVisible: () => {
      return addOnId => {
        return (
          useTenantSubscriptionStore().isAddOnAvailableForPurchase(addOnId) &&
          !useTenantSubscriptionStore().canAccessAddOn(addOnId)
        );
      };
    },
    isAddOnAvailableForAccount: state => {
      return addOnId => {
        return (
          usePricingStore().isAddOnAvailableForCountry(
            state.countryCode,
            addOnId
          ) &&
          (useTenantSubscriptionStore().isAddOnAvailableForPurchase(addOnId) ||
            isEnterprise() ||
            useTenantSubscriptionStore().isCPQCustomer() ||
            isProTrial())
        );
      };
    },
    isEoCustomerPendingMigration: state => {
      return addOnId => {
        const addOn = state.addOns[addOnId];
        return addOn?.status === AddOnStatus.MIGRATION_REQUIRED;
      };
    },

    // Check if legacy EO customers are allowed to access the add-on
    // Eg invoker: frontend/vnext/src/apps/hr/src/modules/upgrade/components/hr-upgrade-pricing-changes-banner.vue
    isEoCustomerAllowed: state => {
      return addOnId => {
        const addOn = state.addOns[addOnId];
        return (
          addOn?.status === AddOnStatus.MIGRATION_REQUIRED &&
          !checkFF('wo-1123-paywall-eo-customer')
        );
      };
    },
    isEoCustomerBlocked: state => {
      return addOnId => {
        const addOn = state.addOns[addOnId];
        return (
          addOn?.status === AddOnStatus.MIGRATION_REQUIRED &&
          checkFF('wo-1123-paywall-eo-customer')
        );
      };
    },

    //
    isAddOnBlocked: () => {
      return addOnId => {
        return !useTenantSubscriptionStore().canAccessAddOn(addOnId);
      };
    },

    // @todo revisit this one
    // Check if the user can access the add-on
    // Eg invoker: frontend/vnext/src/apps/hr/src/modules/forms/components/forms-onboarding.vue
    canAccessAddOn: state => {
      return addOnId => {
        const _this = useTenantSubscriptionStore();
        const addOnStore = useAddOnStore();

        // Get all related add-on IDs or just the add-on ID itself
        const relatedAddOnIds =
          addOnStore.getRelatedAddOnIdsOrJustTheAddOnId(addOnId);

        // Check access for each related add-on ID
        return relatedAddOnIds.some(relatedAddOnId => {
          const betaAccess = _this.hasBetaAccess(relatedAddOnId);
          const featureFlagAccess =
            _this.hasAccessViaFeatureFlag(relatedAddOnId);
          const canAccess = state.addOns[relatedAddOnId]?.canAccess;
          const eoCustomerAccess =
            useTenantSubscriptionStore().isEoCustomerAllowed(relatedAddOnId);
          const proTrialAccess = isProTrial();

          return (
            betaAccess ||
            featureFlagAccess ||
            canAccess ||
            eoCustomerAccess ||
            proTrialAccess
          );
        });
      };
    },

    // Check if the add-on is available for the user
    // Eg invoker: frontend/vnext/src/apps/hr/src/modules/forms/components/forms-onboarding.vue
    isHrFlareVisible: () => {
      return addOnId => {
        const _this = useTenantSubscriptionStore();
        return (
          _this.isAddOnTrialActive(addOnId) ||
          (_this.isAddOnAvailableForAccount(addOnId) &&
            !_this.isSubscribedToAddOn(addOnId))
        );
      };
    },
    getAddOnTrialDaysRemaining: state => {
      return addOnId => {
        return getAddOnTrialDaysRemaining(state.addOns[addOnId]?.trialEndDate);
      };
    },
    getDaysUntilAccessBlocked: () => {
      return () => {
        const dateAccessIsBlocked = new Date(2023, 9, 1);
        const timeDifference = dateAccessIsBlocked - new Date();
        const days = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
        return days;
      };
    },
    isAddOnTrialActive: state => {
      return addOnId => {
        const addOn = state.addOns[addOnId];

        if (!addOn) {
          return false;
        }

        return (
          getAddOnTrialDaysRemaining(addOn.trialEndDate) &&
          addOn.status === AddOnStatus.ACTIVE
        );
      };
    },

    // @todo: check which add-on is ACTIVE and check the trialAvailable for that (either HR Annual or HR Monthly)
    // Eg invoker: frontend/vnext/src/apps/hr/src/modules/upgrade/components/hr-upgrade-pricing-changes-banner.vue
    isAddOnTrialAvailable: state => {
      return addOnId => {
        return (
          (useTenantSubscriptionStore().canUserManageSubscription() ||
            useTenantSubscriptionStore().canCPQUserManageSubscription()) &&
          state.addOns[addOnId]?.trialAvailable
        );
      };
    },

    // Get the trial length for the add-on
    // Eg invoker: frontend/vnext/src/apps/billing-shared/src/components/hr-bundle-subscription-card.vue
    getTrialLength: () => {
      return addOnId => {
        return addOnDataMap[addOnId]?.trialLength;
      };
    },
    getTrialDisplayBillingDate: () => {
      const now = new Date();
      return addOnId => {
        const trialLength = addOnDataMap[addOnId]?.trialLength?.count;
        const featureFlagKey = addOnDataMap[addOnId]?.featureFlagPreSubscribe;
        if (featureFlagKey && isFeatureFlagDateInFuture(featureFlagKey)) {
          const flagDate = getFeatureFlagDate(featureFlagKey);
          return addDaysToDate(flagDate, trialLength);
        }
        return addDaysToDate(now, trialLength);
      };
    },
    getBillingDate: () => {
      return addOnId => {
        const featureFlagKey = addOnDataMap[addOnId]?.featureFlagPreSubscribe;
        if (featureFlagKey && isFeatureFlagDateInFuture(featureFlagKey)) {
          return getFeatureFlagDate(featureFlagKey);
        }

        const now = new Date();

        return now;
      };
    },
    isAutoOptedIn: state => {
      return addOnId => {
        return state.addOns[addOnId]?.createdBy === 'system';
      };
    },
    isDeputyHrGenericBannerVisible: () => {
      return addOnId => {
        return (
          useTenantSubscriptionStore().isAddOnAvailableForPurchase(addOnId) &&
          (useTenantSubscriptionStore().isAddOnTrialAvailable(addOnId) ||
            useTenantSubscriptionStore().isEoCustomerPendingMigration(addOnId))
        );
      };
    },
    isCPQCustomer: () => {
      return () => {
        const channel = getInstallationDetails()?.Channel ?? '';
        return (
          checkFF('wo-981-deputy-hr-cpq-customer') ||
          channel === SUBSCRIPTION_CHANNEL.CPQ
        );
      };
    },
    getPromoCode: () => {
      return (addOnId, promoCode) => {
        if (!(addOnId in ADD_ON_PROMO_CODES)) {
          console.warn(`No promo codes found for add-on ID: ${addOnId}`);
          return null;
        }

        const addOnPromos = ADD_ON_PROMO_CODES[addOnId];
        if (!addOnPromos?.length) {
          return null;
        }

        return addOnPromos.find(promo => promo.code === promoCode) || null;
      };
    },
    getTrialDateWithPromoCode: () => {
      return (addOnId, promoCode) => {
        const _this = useTenantSubscriptionStore();
        const promo = _this.getPromoCode(addOnId, promoCode);
        const trialLength = _this.getTrialLength(addOnId);

        let newTrialLength = trialLength.count;

        const now = DateTime.local();

        const featureFlagKey = addOnDataMap[addOnId]?.featureFlagPreSubscribe;
        if (featureFlagKey && isFeatureFlagDateInFuture(featureFlagKey)) {
          newTrialLength += getFeatureFlagDateDiffInDays(featureFlagKey);
        }

        if (promo.discount === 100) {
          const promoDate = now.plus({ months: promo.periodMonth });
          const promoLengthCount = Math.ceil(promoDate.diff(now, 'days').days);
          newTrialLength = trialLength.count + promoLengthCount;
        }
        return addDaysToDate(now.toJSDate(), newTrialLength);
      };
    },
    getCurrentPlanId: state => {
      return () => {
        return state.planId;
      };
    },
    getChangePlanUrl: () => {
      const webappStore = useWebappStore();

      return planId => {
        return `${OnceRoutes.Base}${OnceRoutes.ChangePlan.replace(
          ':installationId',
          webappStore.getInstanceId()
        ).replace(':planId', planId)}`;
      };
    },
    getPlanNameTracking: () => {
      return planId => {
        return (
          SUBSCRIPTION_NAME_TRACKING[planId] ||
          SUBSCRIPTION_NAME_TRACKING[SUBSCRIPTION.FREE]
        );
      };
    },
  },
  actions: {
    async loadAddOnSubscriptions(forceReload = false) {
      if ((this.addOns.length && !forceReload) || isProTrial()) {
        return;
      }

      try {
        this.isLoading = true;
        this.loadingError = null;

        if (this.loadAddOnSubscriptionsPromise === null) {
          this.loadAddOnSubscriptionsPromise = getAddOnSubscriptions;
        }

        const { data } = await this.loadAddOnSubscriptionsPromise();

        this.addOns = data.productAddOns.reduce((accumulator, addOn) => {
          accumulator[addOn.planIdentifier] = addOn;
          return accumulator;
        }, {});
      } catch (e) {
        this.loadingError = e;
      } finally {
        this.isLoading = false;
        this.loadAddOnSubscriptionsPromise = null;
      }
    },
    async subscribeToAddon(addOnId, isTrial, promoCode, eventDetails) {
      this.isSubscribing = true;
      this.subscribeError = null;
      const trackingDetails = { ...(eventDetails || {}), isTrial };
      this.trackAddOnSubscribeRequestEvent(
        EVENTS.ADD_ON_SUBSCRIBE_REQUESTED,
        addOnId,
        trackingDetails
      );

      if (promoCode) {
        this.trackPromoCodeApplyEvent(
          EVENTS.PROMOTION_ADD_REQUESTED,
          eventDetails.screen,
          addOnId,
          {
            code: promoCode,
          }
        );
      }

      try {
        const data = await subscribeToAddOn(addOnId, isTrial, promoCode);

        this.addOns[addOnId] = data;
        this.isSubscribing = false;
        this.trackAddOnSubscribeRequestEvent(
          EVENTS.ADD_ON_SUBSCRIBE_SUCCESS_ACKNOWLEDGED,
          addOnId,
          trackingDetails
        );

        if (promoCode) {
          this.trackPromoCodeApplyEvent(
            EVENTS.PROMOTION_ADD_SUCCESS_ACKNOWLEDGED,
            eventDetails.screen,
            addOnId,
            {
              code: promoCode,
              description: this.addOns[addOnId].data,
            }
          );
        }

        await this.loadAddOnSubscriptions(true);
        return true;
      } catch (e) {
        this.isSubscribing = false;

        const rawMessage = e?.response?.data?.error?.message || e?.message;

        const errorViolation =
          e?.response?.data?.error?.details?.[0]?.badRequest
            ?.fieldViolations?.[0].field;

        const errorMessageKey =
          SubscriptionErrorToLocaleKey[errorViolation] ||
          SubscriptionErrorToLocaleKey.default;

        this.subscribeError = {
          code: e?.response?.data?.error?.code || 500,
          key: errorMessageKey,
          message: t(`errors:${errorMessageKey}`),
        };

        // Keeping a raw message for data analytics to keep
        // track of the actual error message from BE
        this.trackAddOnSubscribeRequestEvent(
          EVENTS.ADD_ON_SUBSCRIBE_FAILURE_ACKNOWLEDGED,
          addOnId,
          {
            ...trackingDetails,
            error: rawMessage,
          }
        );

        if (promoCode) {
          this.trackPromoCodeApplyEvent(
            EVENTS.PROMOTION_ADD_FAILURE_ACKNOWLEDGED,
            eventDetails.screen,
            addOnId,
            {
              code: promoCode,
              error: {
                message: rawMessage,
              },
            }
          );
        }

        return false;
      }
    },

    // Check whether the user can still access a CANCELLED add-on
    // Eg invoker: frontend/vnext/src/apps/billing-shared/src/components/hr-bundle-subscription-card.vue
    isCancelledAddOnStillActive(addOnId) {
      // 1. Check if the user can access the add-on (is active)
      const canAccess = this.canAccessAddOn(addOnId);

      if (!canAccess) {
        return false;
      }

      // 2. If the user can access the add-on, check if the add-on is cancelled
      const addOnStore = useAddOnStore();

      // Get all related add-on IDs or just the add-on ID itself
      const relatedAddOnIds =
        addOnStore.getRelatedAddOnIdsOrJustTheAddOnId(addOnId);

      // First check: if any add-on is ACTIVE, return false
      const hasActiveAddOn = relatedAddOnIds.some(relatedAddOnId => {
        const addOn = this.addOns[relatedAddOnId];
        if (!addOn) {
          return false;
        }

        return addOn.status === AddOnStatus.ACTIVE;
      });

      if (hasActiveAddOn) {
        return false;
      }

      // Second check: if any add-on is CANCELLED, return true
      return relatedAddOnIds.some(relatedAddOnId => {
        const addOn = this.addOns[relatedAddOnId];
        if (!addOn) {
          return false;
        }

        return addOn.status === AddOnStatus.CANCELLED;
      });
    },

    // Get the cancelled add-on termination date
    // Eg invoker: frontend/vnext/src/apps/billing-shared/src/components/hr-bundle-subscription-card.vue
    getCancelledAddOnTerminationDate(addOnId) {
      return formatDisplayDateShort(this.addOns[addOnId].terminationDate);
    },
    hasAccessViaFeatureFlag(addOnId) {
      if (addOns.HR_ADD_ON_ID == addOnId) {
        return !shouldEnableHrPaywallLogic();
      }

      return false;
    },
    hasBetaAccess(addOnId) {
      if (addOns.HR_ADD_ON_ID == addOnId) {
        return shouldEnableHrBeta();
      }

      return false;
    },

    // Get the trial period for the add-on
    // Eg invoker: frontend/vnext/src/apps/hr/src/modules/upgrade/components/hr-upgrade-pricing-changes-banner.vue
    getTrialPeriod({ addOnId, hasHyphen = false }) {
      if (!this.isAddOnTrialAvailable(addOnId)) {
        return;
      }
      const trialLength = this.getTrialLength(addOnId);

      return hasHyphen
        ? `${trialLength.count}-${trialLength.unitSingular}`
        : `${trialLength.count} ${trialLength.unit}`;
    },
    trackAddOnSubscribeRequestEvent(eventName, addOnId, eventDetails) {
      const analyticsService = getDataAnalyticsService();
      const isTrialValue = eventDetails?.isTrial ?? false;

      analyticsService.track(
        {
          name: eventName,
          screen: eventDetails.screen,
        },
        {
          source: eventDetails.source,
          addOn: {
            details: {
              name: eventDetails.addOnName,
              id: addOnId,
              isTrial: isTrialValue,
              ...(eventDetails.trialEndDate
                ? { trialEndDate: eventDetails.trialEndDate }
                : {}),
              ...(eventDetails.error
                ? { error: { message: eventDetails.error } }
                : {}),
            },
          },
        }
      );
    },
    trackPromoCodeApplyEvent(eventName, eventScreen, addOnId, details) {
      const analyticsService = getDataAnalyticsService();
      analyticsService.track(
        { name: eventName, screen: eventScreen },
        {
          source: `Subscribe to ${addOnDataMap[addOnId].name} confirmation modal - apply code`,
          promotion: {
            details,
          },
        }
      );
    },
    getAddOnLearnMoreUrl(addOnId) {
      const isEoCustomer = this.isEoCustomerPendingMigration(addOnId);
      const addOnLearnMorUrls =
        ADD_ON_LEARN_MORE_URLS[addOnId].tenant[this.countryCode] ??
        ADD_ON_LEARN_MORE_URLS[addOnId].tenant['US'];

      switch (addOnId) {
        case addOns.HR_ADD_ON_ID:
          return isEoCustomer || this.hasCustomPricing()
            ? addOnLearnMorUrls.helpArticle
            : addOnLearnMorUrls.websitePage;
        case addOns.ADVANCED_REPORTING_ADD_ON_ID:
          return addOnLearnMorUrls.helpArticle;
        case addOns.MESSAGING_ADD_ON_ID:
          return addOnLearnMorUrls.helpArticle;
        default:
          return '';
      }
    },
    getPurchasableAddOns() {
      return this.availableAddOns.filter(availableAddOn => {
        const addOn = this.addOns[availableAddOn.addOnId];
        if (
          addOn &&
          [AddOnStatus.ACTIVE, AddOnStatus.INELIGIBLE].includes(addOn.status)
        ) {
          return false;
        }

        return true;
      });
    },
    launchNewMessageModal(messageContent, eventDetails) {
      const router = getRouter();

      this.trackLaunchNewMessageModal(eventDetails);

      VueEventBus.$emit(NEWSFEED_EVENTS.NEWSFEED_SHOW_CREATE_POST, {
        messageContent,
      });

      router.push('#newsfeed');
    },
    trackLaunchNewMessageModal(eventDetails) {
      const analyticsService = getDataAnalyticsService();
      analyticsService.track(
        {
          name: EVENTS.NEWSFEED_POST_ADD_STARTED,
          screen: eventDetails.screen,
        },
        {
          source: eventDetails.source,
          newsfeedPost: {
            details: {
              postType: SHARED_NEWSFEED_POST_TYPE.POST,
            },
          },
        }
      );
    },
    getFeatures(addOnId) {
      return (
        addOnDataMap[addOnId]?.features[this.countryCode]?.[this.planId] ||
        addOnDataMap[addOnId]?.features['WW']?.[this.planId] ||
        []
      );
    },
    isFeatureAvailable(addOnId, featureKey) {
      const features = this.getFeatures(addOnId);
      return features.includes(featureKey);
    },
    getDisplayFeatures(addOnId) {
      const features = this.getFeatures(addOnId);

      const translationMap =
        addOnDataMap[addOnId]?.translations?.features || {};
      const translatedFeatures = features
        .map(featureKey => translationMap[featureKey])
        .filter(Boolean);

      return translatedFeatures;
    },
    getAddOnTrialEndDateISOString(hostname, addOnId) {
      const now = new Date();
      const trialLength = this.getTrialLength(addOnId) || {};
      const trialEndDate =
        this.data[hostname]?.addOnProducts?.[addOnId]?.trialEndDate;
      const endDate = new Date(
        trialEndDate ?? addDaysToDate(now, trialLength.count)
      );

      return endDate.toISOString();
    },
  },
});

export default useTenantSubscriptionStore;
